import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Lock, Loader2, RefreshCw } from "lucide-react";
import { LogoBadge } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (pw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/waitlist`, { params: { password: pw } });
      setRows(res.data);
      setAuthed(true);
    } catch (err) {
      toast.error("Invalid admin password");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    window.open(`${API}/waitlist/export?password=${encodeURIComponent(password)}`, "_blank");
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF7F2] px-5">
        <form
          onSubmit={(e) => { e.preventDefault(); load(password); }}
          className="w-full max-w-sm rounded-[2rem] border border-[#E6E0D4] bg-[#FFFDF9] p-8 shadow-xl"
        >
          <div className="mb-6 flex items-center gap-3">
            <LogoBadge size={44} />
            <div>
              <div className="font-serif text-xl font-semibold text-[#2C2B29]">CBDoodle Admin</div>
              <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#8A8478]">Waitlist Access</div>
            </div>
          </div>
          <label className="mb-2 flex items-center gap-2 font-sans text-sm text-[#5C5850]">
            <Lock size={14} /> Admin password
          </label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="w-full rounded-full border border-[#E6E0D4] bg-white px-5 py-3 font-sans text-[15px] outline-none focus:border-[#C58F3B]"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4A6B5D] px-6 py-3 font-sans text-[15px] font-medium text-white hover:bg-[#2D4338] disabled:opacity-70"
          >
            {loading ? <Loader2 size={17} className="animate-spin" /> : "View Signups"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2] px-5 py-10 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoBadge size={44} />
            <div>
              <h1 className="font-serif text-2xl font-semibold text-[#2C2B29]">Waitlist Signups</h1>
              <p className="font-sans text-sm text-[#5C5850]">{rows.length} total entries</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => load(password)}
              className="inline-flex items-center gap-2 rounded-full border border-[#E6E0D4] bg-[#FFFDF9] px-5 py-2.5 font-sans text-sm font-medium text-[#2C2B29] hover:bg-[#F3EDE2]"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              data-testid="admin-export-btn"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-full bg-[#C58F3B] px-5 py-2.5 font-sans text-sm font-medium text-white hover:bg-[#B07D2B]"
            >
              <Download size={15} /> Export CSV
            </button>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-[#E6E0D4] bg-[#FFFDF9]">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#F3EDE2] font-mono text-[11px] uppercase tracking-[0.12em] text-[#8A8478]">
                <tr>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Poodle</th>
                  <th className="px-5 py-3">Size</th>
                  <th className="px-5 py-3">Joined</th>
                </tr>
              </thead>
              <tbody className="font-sans text-sm text-[#2C2B29]">
                {rows.length === 0 && (
                  <tr><td colSpan={4} className="px-5 py-8 text-center text-[#8A8478]">No signups yet.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[#E6E0D4]">
                    <td className="px-5 py-3">{r.email}</td>
                    <td className="px-5 py-3">{r.dog_name || "—"}</td>
                    <td className="px-5 py-3 capitalize">{r.poodle_size || "—"}</td>
                    <td className="px-5 py-3 text-[#5C5850]">{new Date(r.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
